import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore, useAppStore, api } from "@/store";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  FileSpreadsheet, CheckCircle2, Clock, Send, Lock, Save,
  Download, ExternalLink, Building2, Calendar, BarChart3, RotateCcw, AlertTriangle, Trash2, Layers, CalendarClock
} from "lucide-react";
import { toast } from "sonner";
import { t } from "@/i18n/bg";

const statusConfig = {
  DRAFT: { color: "bg-slate-100 text-slate-700 border-slate-200", icon: Clock, label: t("draft") },
  SUBMITTED: { color: "bg-blue-50 text-blue-700 border-blue-200", icon: Send, label: t("submitted") },
  APPROVED: { color: "bg-green-50 text-green-700 border-green-200", icon: CheckCircle2, label: t("approved") },
  LOCKED: { color: "bg-gray-100 text-gray-700 border-gray-200", icon: Lock, label: t("locked") },
  NEEDS_CORRECTION: { color: "bg-amber-50 text-amber-700 border-amber-200", icon: AlertTriangle, label: t("needsCorrection") },
};

function StatusBadge({ status }) {
  const cfg = statusConfig[status] || statusConfig.DRAFT;
  const Icon = cfg.icon;
  return (
    <span data-testid={`status-badge-${status?.toLowerCase()}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border ${cfg.color}`}>
      <Icon className="w-3 h-3" />{cfg.label}
    </span>
  );
}

function DeadlineBadge({ deadline, status }) {
  if (!deadline) return <span className="text-slate-300 text-xs">—</span>;
  if (status === "APPROVED" || status === "LOCKED") {
    return <span className="text-xs text-slate-400">{new Date(deadline).toLocaleDateString("bg-BG")}</span>;
  }
  const now = new Date(); now.setHours(0,0,0,0);
  const dl = new Date(deadline); dl.setHours(0,0,0,0);
  const diff = Math.ceil((dl - now) / 86400000);
  if (diff < 0) {
    return (
      <span data-testid="deadline-expired" className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-red-50 text-red-600 border border-red-200">
        <CalendarClock className="w-3 h-3" /> {t("deadlineExpired")}
      </span>
    );
  }
  if (diff <= 3) {
    return (
      <span data-testid="deadline-soon" className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-amber-50 text-amber-600 border border-amber-200">
        <CalendarClock className="w-3 h-3" /> {diff} {t("daysLeft")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-slate-500">
      <CalendarClock className="w-3 h-3" /> {new Date(deadline).toLocaleDateString("bg-BG")}
    </span>
  );
}

export default function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const { stats, instances, periods, fetchStats, fetchInstances, fetchPeriods } = useAppStore();
  const [periodFilter, setPeriodFilter] = useState("all");
  const [cuSummary, setCuSummary] = useState([]);
  const [showSummary, setShowSummary] = useState(false);
  const [showBulkDeadline, setShowBulkDeadline] = useState(false);
  const [bulkDeadlineValue, setBulkDeadlineValue] = useState("");
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const navigate = useNavigate();

  useEffect(() => { fetchStats(); fetchInstances(); fetchPeriods(); }, []);

  useEffect(() => {
    if (isAdmin() && periodFilter !== "all") loadCuSummary(periodFilter);
  }, [periodFilter]);

  const loadCuSummary = async (pid) => {
    try {
      const res = await api.get("/dashboard/summary", { params: { period_id: pid } });
      setCuSummary(res.data); setShowSummary(true);
    } catch { setCuSummary([]); }
  };

  const filteredInstances = periodFilter === "all" ? instances : instances.filter((i) => i.period_id === periodFilter);

  const handleExportAll = async (periodId) => {
    try {
      const res = await api.get(`/periods/${periodId}/export_all`, { responseType: "blob" });
      const a = document.createElement("a"); a.href = window.URL.createObjectURL(new Blob([res.data]));
      a.download = `reports_${periodId}.zip`; a.click(); toast.success(t("downloaded"));
    } catch { toast.error(t("exportFailed")); }
  };

  const handleDeleteInstance = async () => {
    if (!deleteDialog || deleteConfirmText !== "DELETE") return;
    try {
      await api.delete(`/instances/${deleteDialog.id}`);
      fetchInstances(); fetchStats();
      toast.success(t("delete"));
    } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
    finally { setDeleteDialog(null); setDeleteConfirmText(""); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 data-testid="dashboard-title" className="text-3xl font-semibold tracking-tight text-[#14532d]">{t("dashboard")}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isAdmin() ? t("centralOverview") : `${t("unitReports")} ${user?.unit_name || ""}`}
          </p>
        </div>
      </div>

      {stats && (
        <div data-testid="stats-grid" className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label={t("totalReports")} value={stats.total_instances} icon={FileSpreadsheet} accent="text-[#14532d]" bg="bg-emerald-50" />
          <StatCard label={t("draft")} value={stats.draft} icon={Clock} accent="text-slate-600" bg="bg-slate-50" />
          <StatCard label={t("submitted")} value={stats.submitted} icon={Send} accent="text-blue-600" bg="bg-blue-50" />
          <StatCard label={t("approved")} value={stats.approved} icon={CheckCircle2} accent="text-green-600" bg="bg-green-50" />
          {(stats.needs_correction > 0 || isAdmin()) && (
            <StatCard label={t("needsCorrection")} value={stats.needs_correction || 0} icon={AlertTriangle} accent="text-amber-600" bg="bg-amber-50" />
          )}
          {isAdmin() && (<>
            <StatCard label={t("templatesCount")} value={stats.templates} icon={FileSpreadsheet} accent="text-amber-600" bg="bg-amber-50" />
            <StatCard label={t("unitsCount")} value={stats.units} icon={Building2} accent="text-purple-600" bg="bg-purple-50" />
            <StatCard label={t("lockedCount")} value={stats.locked} icon={Lock} accent="text-gray-600" bg="bg-gray-50" />
          </>)}
        </div>
      )}

      {isAdmin() && showSummary && cuSummary.length > 0 && (
        <Card data-testid="cu-summary-card" className="border border-slate-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between py-4 px-6 border-b border-slate-100">
            <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-[#14532d]" /> {t("consolidatedSummary")}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/50">
                    <th className="text-left py-2.5 px-4 font-medium text-slate-500 uppercase tracking-wider">{t("unit")}</th>
                    <th className="text-left py-2.5 px-3 font-medium text-slate-500 uppercase tracking-wider">{t("status")}</th>
                    <th className="text-right py-2.5 px-3 font-medium text-slate-500 uppercase tracking-wider">{t("revenue")}</th>
                    <th className="text-right py-2.5 px-3 font-medium text-slate-500 uppercase tracking-wider">{t("expenses")}</th>
                    <th className="text-right py-2.5 px-3 font-medium text-slate-500 uppercase tracking-wider">{t("result")}</th>
                    <th className="text-center py-2.5 px-3 font-medium text-slate-500 uppercase tracking-wider">{t("issues")}</th>
                    <th className="text-right py-2.5 px-4 font-medium text-slate-500 uppercase tracking-wider">{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {cuSummary.map((s) => (
                    <tr key={s.instance_id} data-testid={`summary-row-${s.instance_id}`} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="py-2 px-4 font-medium text-slate-800">{s.unit_name}</td>
                      <td className="py-2 px-3"><StatusBadge status={s.status} /></td>
                      <td className="py-2 px-3 text-right font-mono">{formatNum(s.metrics?.revenue_total)}</td>
                      <td className="py-2 px-3 text-right font-mono">{formatNum(s.metrics?.expense_total)}</td>
                      <td className="py-2 px-3 text-right font-mono">{formatNum(s.metrics?.financial_result)}</td>
                      <td className="py-2 px-3 text-center">
                        {(s.errors > 0 || s.warnings > 0) ? (
                          <span className={`text-xs ${s.errors > 0 ? 'text-red-600' : 'text-amber-600'}`}>
                            {s.errors > 0 && `${s.errors}E `}{s.warnings > 0 && `${s.warnings}W`}
                          </span>
                        ) : <span className="text-slate-300">-</span>}
                      </td>
                      <td className="py-2 px-4 text-right">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/report/${s.instance_id}`)}
                          className="h-7 text-[10px] text-[#14532d] hover:bg-emerald-50">
                          <ExternalLink className="w-3 h-3 mr-0.5" />{t("open")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border border-slate-200 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between py-4 px-6 border-b border-slate-100">
          <CardTitle className="text-lg font-semibold text-slate-800">{t("reportInstances")}</CardTitle>
          <div className="flex items-center gap-3">
            <Select value={periodFilter} onValueChange={setPeriodFilter}>
              <SelectTrigger data-testid="period-filter" className="w-[200px] h-9 text-sm"><SelectValue placeholder={t("filterByPeriod")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allPeriods")}</SelectItem>
                {periods.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.year})</SelectItem>)}
              </SelectContent>
            </Select>
            {isAdmin() && periodFilter !== "all" && (
              <Button data-testid="export-all-button" variant="outline" size="sm" onClick={() => handleExportAll(periodFilter)} className="h-9">
                <Download className="w-4 h-4 mr-1" /> {t("exportAll")}
              </Button>
            )}
            {isAdmin() && periodFilter !== "all" && (
              <Button data-testid="consolidated-report-button" size="sm"
                onClick={() => navigate(`/consolidated/${periodFilter}`)}
                className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white">
                <Layers className="w-4 h-4 mr-1" /> {t("consolidatedReport")}
              </Button>
            )}
            {isAdmin() && periodFilter !== "all" && (
              <Button data-testid="bulk-deadline-button" variant="outline" size="sm"
                onClick={() => { setBulkDeadlineValue(""); setShowBulkDeadline(true); }}
                className="h-9 text-slate-600 border-slate-300 hover:bg-slate-50">
                <CalendarClock className="w-4 h-4 mr-1" /> {t("bulkDeadline")}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="text-left py-3 px-6 font-medium text-slate-500 text-xs uppercase tracking-wider">{t("unit")}</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-500 text-xs uppercase tracking-wider">{t("template")}</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-500 text-xs uppercase tracking-wider">{t("period")}</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-500 text-xs uppercase tracking-wider">{t("status")}</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-500 text-xs uppercase tracking-wider">{t("deadline")}</th>
                  <th className="text-right py-3 px-6 font-medium text-slate-500 text-xs uppercase tracking-wider">{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredInstances.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-12 text-muted-foreground">{t("noInstances")}{isAdmin() && t("noInstancesAdmin")}</td></tr>
                ) : filteredInstances.map((inst) => (
                  <tr key={inst.id} data-testid={`instance-row-${inst.id}`} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                    <td className="py-3 px-6 font-medium text-slate-800">{inst.unit_name}</td>
                    <td className="py-3 px-4 text-slate-600">{inst.template_name}</td>
                    <td className="py-3 px-4 text-slate-600">{inst.period_name} ({inst.period_year})</td>
                    <td className="py-3 px-4"><StatusBadge status={inst.status} /></td>
                    <td className="py-3 px-4"><DeadlineBadge deadline={inst.deadline} status={inst.status} /></td>
                    <td className="py-3 px-6 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button data-testid={`open-report-${inst.id}`} variant="ghost" size="sm"
                          onClick={() => navigate(`/report/${inst.id}`)} className="h-8 text-xs text-[#14532d] hover:text-[#166534] hover:bg-emerald-50">
                          <ExternalLink className="w-3.5 h-3.5 mr-1" /> {t("open")}
                        </Button>
                        {isAdmin() && inst.status === "SUBMITTED" && (
                          <Button data-testid={`approve-${inst.id}`} size="sm"
                            onClick={async () => { await api.post(`/instances/${inst.id}/approve`); fetchInstances(); fetchStats(); toast.success(t("reportApproved")); }}
                            className="h-8 text-xs bg-green-600 hover:bg-green-700 text-white">
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {t("approve")}
                          </Button>
                        )}
                        {isAdmin() && (inst.status === "APPROVED" || inst.status === "LOCKED") && (
                          <Button data-testid={`reopen-${inst.id}`} variant="outline" size="sm"
                            onClick={async () => { await api.post(`/instances/${inst.id}/reject`, { reason: "Reopened" }); fetchInstances(); fetchStats(); toast.success(t("reportReopened")); }}
                            className="h-8 text-xs text-amber-600 border-amber-200 hover:bg-amber-50">
                            <RotateCcw className="w-3.5 h-3.5 mr-1" /> {t("reopen")}
                          </Button>
                        )}
                        {isAdmin() && (
                          <Button data-testid={`delete-instance-${inst.id}`} variant="ghost" size="sm"
                            onClick={() => { setDeleteDialog(inst); setDeleteConfirmText(""); }}
                            className="h-8 text-xs text-red-500 hover:text-red-700 hover:bg-red-50">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Deadline Dialog */}
      <Dialog open={showBulkDeadline} onOpenChange={setShowBulkDeadline}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <CalendarClock className="w-4 h-4 text-slate-500" /> {t("bulkDeadline")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-sm text-slate-600">{t("bulkDeadlineDesc")}</p>
            <Input data-testid="bulk-deadline-input" type="date" value={bulkDeadlineValue}
              onChange={(e) => setBulkDeadlineValue(e.target.value)} className="h-10" />
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowBulkDeadline(false)}>{t("close")}</Button>
            <Button data-testid="bulk-deadline-save" size="sm" onClick={async () => {
              try {
                const res = await api.patch("/instances/bulk-deadline", { period_id: periodFilter, deadline: bulkDeadlineValue || null });
                toast.success(`${t("bulkDeadlineUpdated")} (${res.data.modified})`);
                setShowBulkDeadline(false);
                fetchInstances();
              } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
            }} className="bg-[#14532d] hover:bg-[#166534] text-white">
              <Save className="w-4 h-4 mr-1" /> {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Instance Confirmation Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={(open) => { if (!open) { setDeleteDialog(null); setDeleteConfirmText(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="w-5 h-5 text-red-500" /> {t("permanentDeleteConfirm")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <p className="text-sm text-slate-700">{t("permanentDeleteWarning")}</p>
            {deleteDialog && (
              <p className="text-sm font-medium text-slate-900">{deleteDialog.unit_name} — {deleteDialog.period_name}</p>
            )}
            <div>
              <Label className="text-xs text-slate-500">{t("typeDeleteConfirm")}</Label>
              <Input data-testid="delete-confirm-input" value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE" className="mt-1 font-mono" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setDeleteDialog(null); setDeleteConfirmText(""); }}>{t("close")}</Button>
            <Button data-testid="confirm-delete-instance-button" size="sm"
              disabled={deleteConfirmText !== "DELETE"}
              onClick={handleDeleteInstance}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40">
              <Trash2 className="w-4 h-4 mr-1" /> {t("delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function formatNum(val) {
  if (val === undefined || val === null) return "-";
  const n = parseFloat(val);
  if (isNaN(n)) return val;
  return n.toLocaleString("bg-BG", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function StatCard({ label, value, icon: Icon, accent, bg }) {
  return (
    <Card data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
      className="border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
            <p className="text-2xl font-semibold tracking-tight text-slate-800">{value}</p>
          </div>
          <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center`}><Icon className={`w-5 h-5 ${accent}`} /></div>
        </div>
      </CardContent>
    </Card>
  );
}
